import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useUser } from '@hooks/useUser';
import {
  getOperationIncident,
  getOperationIncidentNotes,
  getOperationSummary,
  OperationActivitySummary,
  OperationIncident,
  OperationIncidentNote,
  OperationSummary
} from '@networkRequest';
import { PageNavigation, Toast } from '@components';
import { PagePaths } from '../../../utils/PagePaths';
import dayjs from 'dayjs';
import Utils from '../../../utils/utils';
import {
  DeleteIncidentNoteDialog,
  EditIncidentDateTimeDialog,
  EditIncidentDetailDialog,
  EditIncidentLocationDialog,
  EditIncidentPersonnelDialog,
  EditIncidentNoteDialog,
  IncidentStatus,
  IncidentType,
  IncidentPersonnel,
  IncidentDetailItem,
  IncidentNotes
} from './components';
import { COPY, PAGE_PATHS } from '../../../constants';


const OperationIncidentPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useUser();
  const { operationId, incidentId } = useParams<{ operationId: string; incidentId: string }>();

  const [loading, setLoading] = useState<boolean>(false);
  const [operation, setOperation] = useState<OperationSummary | null>(null);
  const [incident, setIncident] = useState<OperationIncident | null>(null);
  const [incidentActivity, setIncidentActivity] = useState<OperationActivitySummary | null>(null);
  const [notes, setNotes] = useState<OperationIncidentNote[]>([]);
  const [selectedIncidentNote, setSelectedIncidentNote] = useState<OperationIncidentNote | null>(null);

  const [editIncidentOpen, setEditIncidentOpen] = useState<boolean>(false);
  const [editLocationOpen, setEditLocationOpen] = useState<boolean>(false);
  const [editDateTimeOpen, setEditDateTimeOpen] = useState<boolean>(false);
  const [editPersonnelOpen, setEditPersonnelOpen] = useState<boolean>(false);
  const [editNoteOpen, setEditNoteOpen] = useState<boolean>(false);
  const [deleteNoteOpen, setDeleteNoteOpen] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);

  const { streetAddress, cityStateZip } = Utils.formatAddress({
    street_address_1: incident?.location?.street_address_1 ?? '',
    street_address_2: incident?.location?.street_address_2 ?? '',
    city: incident?.location?.city ?? '',
    state: incident?.location?.state ?? '',
    zip: incident?.location?.zip ?? '',
  });

  const fetchOperation = useCallback(async () => {
    if (!operationId || !user?.accessToken) return;

    const { data, error } = await getOperationSummary(user.accessToken, operationId);
    if (data) {
      setOperation(data);
    } else {
      setError(error ?? 'Failed to load operation');
    }
  }, [user?.accessToken, operationId]);

  const fetchOperationIncident = useCallback(async () => {
    if (!incidentId || !user?.accessToken) return;

    const { data: _incident, error } = await getOperationIncident({
      accessToken: user?.accessToken,
      incidentId
    });

    if (_incident) {
      setIncident(_incident);
      setIncidentActivity(_incident.activity);
    } else {
      setError(error ?? 'Failed to load incident');
      return;
    }
  }, [user?.accessToken, incidentId]);

  const fetchOperationIncidentNotes = useCallback(async () => {
    if (!incidentId || !user?.accessToken) return;

    const { data, error } = await getOperationIncidentNotes({ accessToken: user.accessToken, incidentId });
    if (data) {
      setNotes(data);
    } else {
      setError(error ?? 'Failed to load incident notes');
    }
  }, [user?.accessToken, incidentId]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    // Load operation and incidents
    const fetchData = async () => {
      try {
        await Promise.all([
          fetchOperation(),
          fetchOperationIncident(),
          fetchOperationIncidentNotes()
        ]);
      } catch {
        setError('Failed to load operation incident data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [operationId, user?.accessToken, fetchOperation, fetchOperationIncident, fetchOperationIncidentNotes]);

  const handleCreateNote = (newNote: OperationIncidentNote) => {
    setNotes((prevNotes) => ([
      newNote,
      ...prevNotes
    ]))
  };

  const handleEditNote = async () => {
    await fetchOperationIncidentNotes();
  };

  const handleDeleteNote = (noteId: string) => {
    setNotes((prevNotes) => prevNotes.filter((note) => note.id !== noteId));
  };

  if (loading) {
    return (
      <Grid
        container
        sx={{
          height: "100%",
          justifyContent: "center",
          alignItems: "center"
        }}>
        <CircularProgress />
      </Grid>
    );
  }

  if (!operationId || !operation || !incidentId || !incident) {
    return null
  }

  const activityPagePath = PagePaths.operationActivities(operationId, incidentActivity?.id);

  return (
    <>
      <PageNavigation
        breadcrumbs={[
          { display: COPY.titles.operations, path: PAGE_PATHS.operations },
          { display: operation.title, path: `${PAGE_PATHS.operations}/${operation.id}` },
          { display: 'Incidents', path: `${PAGE_PATHS.operations}/${operation.id}/incidents` },
        ]}
      />
      <Grid
        container
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'flex-start',
          mt: '32px'
        }}>
        <Grid container size={9} sx={{
          pr: '32px'
        }}>
          <Grid container
            size={12}
            onClick={() => setEditIncidentOpen(true)}
            sx={{
              p: '16px',
              borderRadius: '10px',
              '&:hover': {
                bgcolor: theme.palette.grey[50]
              },
              '&:hover .hover-icon': {
                display: 'block'
              }
            }}
          >
            <Grid container size={12} sx={{
              justifyContent: 'space-between'
            }}>
              <Typography variant='body1' aria-label='Case Number' component='span'>
                {`Case Number: ${incident.case_number ?? ''}`}
              </Typography>
              <EditIcon
                className='hover-icon'
                sx={{
                  width: '15px',
                  height: '15px',
                  color: theme.palette.action.active,
                  display: 'none'
                }}
              />
            </Grid>
            <Grid size={12} sx={{
              mt: '8px'
            }}>
              <Typography component='h1' variant='h4'>{incident.title}</Typography>
            </Grid>
          </Grid>
          <IncidentNotes
            incidentId={incidentId}
            incidentActivity={incidentActivity}
            activityPagePath={activityPagePath}
            notes={notes}
            onCreate={handleCreateNote}
            onEdit={() => setEditNoteOpen(true)}
            onDelete={() => setDeleteNoteOpen(true)}
            setSelectedItem={setSelectedIncidentNote}
            onError={setError}
          />
        </Grid>
        <Grid size={3}>
          <IncidentStatus incident={incident} setIncident={setIncident} setError={setError} />

          <IncidentDetailItem
            name='Date & Time'
            onClick={() => setEditDateTimeOpen(true)}
          >
            <Typography variant='body1'>
              {dayjs(incident.performed_at).format('ddd. MMM. DD YYYY') ?? '\u00A0'}
            </Typography>
            <Typography variant='body1'>
              {dayjs(incident.performed_at).format('h:mm A') ?? '\u00A0'}
            </Typography>
          </IncidentDetailItem>

          <IncidentType
            loading={loading}
            incident={incident}
            setIncident={setIncident}
            setError={setError}
          />

          <IncidentDetailItem
            name='Location'
            onClick={() => setEditLocationOpen(true)}
          >
            <Typography variant='body1'>{incident?.location?.name ?? '\u00A0'}</Typography>
            <Typography variant='body1'>{streetAddress ?? '/u00A0'}</Typography>
            <Typography variant='body1'>{cityStateZip ?? '/u00A0'}</Typography>
          </IncidentDetailItem>

          <IncidentPersonnel incident={incident} setEditPersonnelOpen={setEditPersonnelOpen} />
        </Grid>
      </Grid>
      <EditIncidentDetailDialog
        open={editIncidentOpen}
        onClose={() => setEditIncidentOpen(false)}
        incident={incident}
        setIncident={setIncident}
        setError={setError}
      />
      <EditIncidentDateTimeDialog
        incident={incident}
        open={editDateTimeOpen}
        onClose={() => setEditDateTimeOpen(false)}
        setIncident={setIncident}
        setError={setError}
      />
      <EditIncidentLocationDialog
        open={editLocationOpen}
        onClose={() => setEditLocationOpen(false)}
        incident={incident}
        setIncident={setIncident}
        setError={setError}
      />
      <EditIncidentPersonnelDialog
        open={editPersonnelOpen}
        onClose={() => setEditPersonnelOpen(false)}
        operationId={operation.id}
        incident={incident}
        setIncident={setIncident}
        setError={setError}
      />
      <EditIncidentNoteDialog
        open={editNoteOpen}
        onClose={() => setEditNoteOpen(false)}
        selectedIncidentNote={selectedIncidentNote}
        setSelectedIncidentNote={setSelectedIncidentNote}
        setError={setError}
        onEditIncidentNote={handleEditNote}
      />
      <DeleteIncidentNoteDialog
        open={deleteNoteOpen}
        onClose={() => setDeleteNoteOpen(false)}
        onDelete={handleDeleteNote}
        selectedIncidentNote={selectedIncidentNote}
        setSelectedIncidentNote={setSelectedIncidentNote}
        setError={setError}
      />
      {
        error ? <Toast type='error' onClose={() => setError(null)}>{error}</Toast> : null
      }
    </>
  );
}

export default OperationIncidentPage;
